import { View } from 'backbone.marionette';
import Radio from 'backbone.radio';

import showErrorMessage from "@/util/showErrorMessage";
import showModal from "@/util/showModal";
import showSuccessMessage from '@/util/ShowSuccessMessage';

import GetAllCustomerResponsibilityPagesByFormID from '../../services/GetAllCustomerResponsibilityPagesByFormID';
import HandleOrderSubmission from "../../services/HandleOrderSubmission";
import ValidateOrderModule from "../../services/ValidateOrderModule";

import CustomerResponsibilitiesModalView from '../CustomerResponsibilitiesModalView/CustomerResponsibilitiesModalView';

const SubmittableOrderView = View.extend({

  async onSubmit() {
    let orderDetails = this.model.get('orderDetails');

    /**
     * In most order views, 'orderDetails' is actually assigned the variable named 'orderData' from OrderPage,
     * which itself contains an 'orderDetails' property (i.e., orderDetails.orderDetails).
     * In OrderAttachmentsView, however, 'orderDetails' is already the correct object.
     */
    if (orderDetails.orderDetails) {
      orderDetails = orderDetails.orderDetails
    }

    const { createdOn, commandId, orderId, orderTypeId } = orderDetails;

    if(this.isPastFiscalYearCutoff(createdOn)){
      this.showFiscalYearError();
      return;
    }

    const isValid = await ValidateOrderModule.validate();
    if (!isValid) return;

    if (EXTERNAL) {
      let pages;
      try {
        pages = await GetAllCustomerResponsibilityPagesByFormID(orderTypeId);
      } catch (err) {
        this.showSubmissionError(orderId);
        return;
      }

      if (pages.length && !this.model.get('hasSignedAcknowledgment')) {
        await this.showCustomerResponsibilitiesModal(commandId, orderId, orderTypeId, pages);
      } else {
        await this.submitOrder(orderId, commandId);
      }
    } else {
      await this.submitOrder(orderId);
    }
  },

  async showCustomerResponsibilitiesModal(commandId, orderId, orderTypeId, pages) {
    try {
      const markAcknowledgmentSigned = () => this.model.set('hasSignedAcknowledgment', true);
      const models = pages.map(({ href }) => ({ href, viewed: false }));

      const customerResponsibilitiesModalView = new CustomerResponsibilitiesModalView({
        model: new Backbone.Model({ commandId, orderId, orderTypeId, markAcknowledgmentSigned }),
        collection: new Backbone.Collection(models)
      });

      showModal({
        view: customerResponsibilitiesModalView,
        title: 'Customer Responsibilities',
        dialogClasses: 'modal-xl'
      });
    } catch (err) {
      this.showCustomerResponsibilitiesError();
    }
  },

  async submitOrder(orderId, commandId) {
    try {
      await HandleOrderSubmission(orderId);
      this.showSubmissionSuccess(orderId);

      if (commandId) {
        await Radio.channel('navigate').request('show:ext:dashboard', commandId);
      } else {
        await Radio.channel('navigate').request('show:int:dashboard');
      }

    } catch (err) {
      this.showSubmissionError(orderId);
    }
  },

  isPastFiscalYearCutoff(createdOnStr) {
    const createdOnDate = new Date(createdOnStr);
    let fiscalYear = createdOnDate.getFullYear();
    if (createdOnDate.getMonth() > 8) fiscalYear++; // fiscal year starts in october
    const cutoff = new Date(fiscalYear, 9);
    return Date.now() >= cutoff;
  },

  showSubmissionSuccess(orderId) {
    showSuccessMessage({
      code: 'submissionSuccess',
      title: 'Submit Success',
      message: `Order ${orderId} has been submitted.`,
      hideAfter: 5
    });
  },

  showSubmissionError(orderId) {
    showErrorMessage({
      errorCode: 'submissionError',
      errorTitle: 'Submission Error',
      errorMessage: `There was an error submitting order ${orderId}. Please try again or contact an administrator if
        the issue persists.`
    });
  },

  showFiscalYearError() {
    showErrorMessage({
      errorCode: 'fiscalYearError',
      errorTitle: 'Fiscal Year has passed',
      errorMessage: `Your order cannot be submitted because it is outside of the current fiscal year. Please delete
        your draft and create a new order under the current fiscal year.`
    });
  },

  showCustomerResponsibilitiesError() {
    showErrorMessage({
      errorCode: 'customerResponsibilitiesError',
      errorTitle: 'Document Load Error',
      errorMessage: `An error occurred while loading the customer responsibilities document. Please try again or contact
        an administrator if the issue persists.`
    });
  }
});

export default SubmittableOrderView;
